/// <reference path="../../types.d.ts" />
import { state } from "../core/state";
import { normalizeText, escapeHtml, formatLifeYears, extractNumericYear } from "../core/helpers";
import { FEATURED_BRANCHES } from "../core/constants";
import { getBranchPersonIds } from "../engine/graph";

export interface MapContext {
  switchView: (viewName: any) => void;
  focusPersonInTree: (pid: string) => void;
  openPersonDrawer: (pid: string) => void;
  closePersonDrawer: () => void;
}

let mapCtx: MapContext = {
  switchView: () => {},
  focusPersonInTree: () => {},
  openPersonDrawer: () => {},
  closePersonDrawer: () => {},
};

export function setupMigrationMap(context: MapContext): void {
  mapCtx = context;
}
/* ================= VIEW 3: GEOGRAPHICAL MIGRATION MAP (HISTORICAL ATLAS) ================= */
const MAP_MIN_LON = 16.0, MAP_MAX_LON = 44.5;
const MAP_MIN_LAT = 34.5, MAP_MAX_LAT = 45.5;
const MAP_WIDTH = 1200, MAP_HEIGHT = 680;

function projectMapCoord(lat, lon) {
  const x = ((lon - MAP_MIN_LON) / (MAP_MAX_LON - MAP_MIN_LON)) * MAP_WIDTH;
  const y = ((MAP_MAX_LAT - lat) / (MAP_MAX_LAT - MAP_MIN_LAT)) * MAP_HEIGHT;
  return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
}

function makeArcPath(p1, p2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const midX = (p1.x + p2.x) / 2;
  const midY = (p1.y + p2.y) / 2 - Math.min(50, Math.max(14, dist * 0.16));
  return `M ${p1.x},${p1.y} Q ${midX.toFixed(1)},${midY.toFixed(1)} ${p2.x},${p2.y}`;
}

const MAP_CITIES = [
  {
    id: "niksic",
    name: "Niksiç (Nikšić)",
    lat: 42.7756,
    lon: 18.9444,
    region: "Karadağ (Montenegro)",
    roleDesc: "Ailemizin 19. yüzyıldaki kadim kökü ve ata ocağı (1878 öncesi).",
    isRoot: true,
    era: 1870,
    offsetX: 0,
    offsetY: -20,
    keywords: ["niksic", "niksiç", "karadag", "karadağ", "montenegro"]
  },
  {
    id: "berane",
    name: "Berane",
    lat: 42.8425,
    lon: 19.8733,
    region: "Lim Vadisi / Karadağ",
    roleDesc: "93 Harbi (1877-78) sonrası Sancak ve Lim vadisinde ilk durak.",
    era: 1878,
    offsetX: 0,
    offsetY: 18,
    keywords: ["berane", "lim"]
  },
  {
    id: "mitrovica",
    name: "Mitroviça",
    lat: 42.8914,
    lon: 20.8660,
    region: "Kosova / Sancak",
    roleDesc: "Anadolu'ya geçiş öncesi dedelerimizin doğduğu ve ikamet ettiği merkez.",
    era: 1885,
    offsetX: 0,
    offsetY: -18,
    keywords: ["mitrovic", "mitroviç", "kosov"]
  },
  {
    id: "istanbul",
    name: "İstanbul",
    lat: 41.0082,
    lon: 28.9784,
    region: "Payitaht / Marmara",
    roleDesc: "Balkan Harbi öncesi ve sonrası Osmanlı payitahtına hicret & yerleşim.",
    isHub: true,
    era: 1890,
    offsetX: 0,
    offsetY: -18,
    keywords: ["istanbul", "eminönü", "eminonu", "kadıköy", "kadikoy", "feriköy", "ferikoy", "üsküdar", "fatih"]
  },
  {
    id: "karacabey",
    name: "Karacabey",
    lat: 40.2153,
    lon: 28.3581,
    region: "Bursa / Marmara",
    roleDesc: "Balkan Harbi sonrası muhacir iskânı ve tarımsal yerleşim.",
    era: 1912,
    offsetX: -44,
    offsetY: -14,
    keywords: ["karacabey", "molva"]
  },
  {
    id: "burhaniye",
    name: "Burhaniye",
    lat: 39.4986,
    lon: 26.9728,
    region: "Körfez / Balıkesir (Merkez Yurt)",
    roleDesc: "Ailemizin Türkiye'deki ana yurdu; Geriş Mezarlığı ve Pelitköy merkezli nesiller.",
    isHub: true,
    era: 1915,
    offsetX: 0,
    offsetY: 22,
    keywords: ["burhaniye", "geriş", "geris", "pelitköy", "ören", "balıkesir", "balikesir"]
  },
  {
    id: "edremit",
    name: "Edremit",
    lat: 39.5961,
    lon: 27.0244,
    region: "Körfez / Balıkesir",
    roleDesc: "Körfez içi ticaret ve yerleşim hattı.",
    era: 1925,
    offsetX: 42,
    offsetY: -12,
    keywords: ["edremit", "akçay", "altınoluk"]
  },
  {
    id: "bursa",
    name: "Bursa",
    lat: 40.1885,
    lon: 29.0610,
    region: "Güney Marmara",
    roleDesc: "Karacabey ve Körfez'den vilayet merkezine intikal.",
    era: 1930,
    offsetX: 40,
    offsetY: 14,
    keywords: ["bursa", "nilüfer", "osmangazi", "yıldırım", "mudanya"]
  },
  {
    id: "eskisehir",
    name: "Eskişehir",
    lat: 39.7667,
    lon: 30.5256,
    region: "İç Batı Anadolu",
    roleDesc: "Demiryolu hattı, kamu görevi ve eğitim vesilesiyle yerleşim.",
    era: 1935,
    offsetX: 0,
    offsetY: 18,
    keywords: ["eskişehir", "eskisehir", "tepebaşı", "odunpazarı"]
  },
  {
    id: "ankara",
    name: "Ankara",
    lat: 39.9334,
    lon: 32.8597,
    region: "Başkent / İç Anadolu",
    roleDesc: "Cumhuriyet bürokrasisi, üniversite ve kamu hizmeti odağı.",
    isHub: true,
    era: 1940,
    offsetX: 0,
    offsetY: -18,
    keywords: ["ankara", "çankaya", "cankaya", "yenimahalle", "keçiören"]
  },
  {
    id: "karabuk",
    name: "Karabük",
    lat: 41.2061,
    lon: 32.6204,
    region: "Batı Karadeniz",
    roleDesc: "Demir-Çelik fabrikaları ve sanayi hamlesiyle memuriyet durağı.",
    era: 1948,
    offsetX: -42,
    offsetY: -16,
    keywords: ["karabük", "karabuk"]
  },
  {
    id: "safranbolu",
    name: "Safranbolu",
    lat: 41.2508,
    lon: 32.6942,
    region: "Batı Karadeniz",
    roleDesc: "Tarihi konaklar diyarı ve Karabük sanayi yerleşimi uzantısı.",
    era: 1952,
    offsetX: 46,
    offsetY: 14,
    keywords: ["safranbolu"]
  },
  {
    id: "aydin",
    name: "Aydın",
    lat: 37.8444,
    lon: 27.8458,
    region: "Ege",
    roleDesc: "Ege tarım, ticaret ve ziraat hattı.",
    era: 1955,
    offsetX: 38,
    offsetY: -12,
    keywords: ["aydın", "aydin", "efeler"]
  },
  {
    id: "soke",
    name: "Söke",
    lat: 37.7533,
    lon: 27.4097,
    region: "Ege / Menderes Ovası",
    roleDesc: "Aydın'dan Söke ovasına ziraat ve yerleşim.",
    era: 1960,
    offsetX: -38,
    offsetY: 16,
    keywords: ["söke", "soke"]
  },
  {
    id: "antalya",
    name: "Antalya",
    lat: 36.8969,
    lon: 30.7133,
    region: "Akdeniz",
    roleDesc: "Akdeniz sahil hattına göç ve emeklilik dönemi.",
    era: 1970,
    offsetX: 0,
    offsetY: 18,
    keywords: ["antalya", "muratpaşa", "konyaaltı", "alanya"]
  },
  {
    id: "kars",
    name: "Kars",
    lat: 40.6013,
    lon: 43.0975,
    region: "Doğu Anadolu",
    roleDesc: "Vatan sathında kamu görevi ve mülki hizmetler.",
    era: 1975,
    offsetX: 0,
    offsetY: -18,
    keywords: ["kars", "sarıkamış"]
  }
];

const MIGRATION_ROUTES = [
  { from: "niksic", to: "berane", year: 1878, label: "1878 Berlin Antlaşması Sonrası Lim Vadisine Geçiş" },
  { from: "berane", to: "mitrovica", year: 1885, label: "1885 Sancak & Kosova Hattı" },
  { from: "mitrovica", to: "istanbul", year: 1890, label: "1890 Başkent İstanbul'a Hicret & İntikal" },
  { from: "istanbul", to: "karacabey", year: 1912, label: "1912 Balkan Harbi Sonrası Karacabey İskânı" },
  { from: "karacabey", to: "burhaniye", year: 1915, label: "1915 Karacabey'den Burhaniye Geriş'e Yerleşim" },
  { from: "istanbul", to: "burhaniye", year: 1920, label: "1920 İstanbul - Burhaniye Aile Bağı" },
  { from: "burhaniye", to: "edremit", year: 1925, label: "Körfez İçi Yayılım (Burhaniye - Edremit)" },
  { from: "karacabey", to: "bursa", year: 1930, label: "Karacabey'den Bursa Merkezine Geçiş" },
  { from: "burhaniye", to: "eskisehir", year: 1935, label: "Demiryolu ve Memuriyet: Eskişehir Hattı" },
  { from: "burhaniye", to: "ankara", year: 1940, label: "Cumhuriyet Başkenti Ankara'ya İntikal" },
  { from: "burhaniye", to: "karabuk", year: 1948, label: "Demir-Çelik Sanayii & Memuriyet: Karabük" },
  { from: "karabuk", to: "safranbolu", year: 1952, label: "Karabük - Safranbolu Yerleşimi" },
  { from: "burhaniye", to: "aydin", year: 1955, label: "Ege Tarım ve Ticaret Hattı: Aydın" },
  { from: "aydin", to: "soke", year: 1960, label: "Aydın'dan Söke'ye Yerleşim" },
  { from: "ankara", to: "antalya", year: 1970, label: "Akdeniz'e Göç: Antalya" },
  { from: "ankara", to: "kars", year: 1975, label: "Kamu Görevi & Hizmet: Kars" }
];

const MAP_STORY_CHAPTERS = [
  {
    step: 1,
    titleTr: "1. Kökler: Niksiç & Karadağ (1870'ler)",
    titleEn: "1. Roots: Nikšić & Montenegro (1870s)",
    focusCity: "niksic",
    year: 1878,
    scale: 1.4,
    panX: 180,
    panY: 40,
    textTr: "Ailemizin kökleri Karadağ'ın kadim şehri Niksiç'e (Nikšić) dayanmaktadır. 1877-1878 Osmanlı-Rus Harbi (93 Harbi) ve Berlin Antlaşması sonrasında Niksiç'in Osmanlı idaresinden çıkmasıyla ecdadımız atalarının asırlık yurdunu terk etmek zorunda kalmıştır. Büyük dedelerimiz bu zorlu dönemeçte ilk olarak Lim vadisindeki Berane ve Mitroviça güzergâhına çekilmiştir.",
    textEn: "Our family roots trace back to the ancient Montenegrin city of Nikšić. Following the 1877-1878 Russo-Turkish War and the Treaty of Berlin, when Nikšić was ceded from Ottoman administration, our ancestors were forced to leave their centuries-old homeland, taking refuge initially in Berane along the Lim Valley and Mitrovica."
  },
  {
    step: 2,
    titleTr: "2. Büyük Göç & Payitaht İstanbul (1890 - 1912)",
    titleEn: "2. The Great Migration & Imperial Istanbul (1890 - 1912)",
    focusCity: "istanbul",
    year: 1912,
    scale: 1.25,
    panX: -80,
    panY: -20,
    textTr: "Balkanlar'daki çalkantılı süreç ve Balkan Harbi eşiğinde ecdadımız payitaht İstanbul'a intikal etmiştir. Eminönü, Kadıköy ve tarihi semtlerde ilk sığınaklarını bulan aile bireylerinin bir kısmı Karacabey ovasına iskân edilmiş, toprağa ve yeni vatana burada yeniden kök salmaya başlamıştır.",
    textEn: "Amidst the Balkan turmoil and on the eve of the Balkan Wars, our ancestors arrived in imperial Istanbul. Finding initial shelter in Eminönü and Kadıköy, family branches were subsequently settled in fertile Karacabey, planting new roots in Anatolian soil."
  },
  {
    step: 3,
    titleTr: "3. Yeni Ata Yurdu: Burhaniye ve Körfez (1915 - 1940)",
    titleEn: "3. The New Heart: Burhaniye & The Gulf (1915 - 1940)",
    focusCity: "burhaniye",
    year: 1935,
    scale: 1.5,
    panX: -20,
    panY: -120,
    textTr: "Karacabey ve İstanbul hattından sonra ailemizin ana karargâhı Balıkesir Körfezi'nin incisi Burhaniye olmuştur. Burhaniye Geriş Mezarlığı ve Pelitköy, ailemizin kurucu kuşaklarının ebedi istirahatgâhı haline gelmiştir. Burhaniye, Niksiç'ten sonra ailemizin Türkiye'deki ikinci ana vatanı ve kalbi olmuştur.",
    textEn: "Burhaniye in the Gulf of Edremit became the permanent spiritual home of the family. The historic Geriş Cemetery and Pelitköy became the resting place of our founding generations, serving as the family's second heartland after Nikšić."
  },
  {
    step: 4,
    titleTr: "4. Anadolu'ya Yayılım & Günümüz (1940 - 2026)",
    titleEn: "4. Anatolian Legacy & Today (1940 - 2026)",
    focusCity: "ankara",
    year: 2026,
    scale: 1.0,
    panX: 0,
    panY: 0,
    textTr: "Cumhuriyetimizin kuruluşuyla birlikte yeni kuşaklar; eğitim, kamu hizmeti, sanayi ve ticaret vesilesiyle Ankara, Eskişehir, Karabük, Safranbolu, Bursa, Aydın, Söke, Antalya ve Kars'a kadar Anadolu'nun dört bir yanına yayılmıştır. Bugün Niksiç'ten başlayan bu kutlu yolculuk 150 yıllık ulu bir çınar gibi tüm vatanda kök salmıştır.",
    textEn: "With the Republic, successive generations branched out into public service, industry, and commerce across Ankara, Eskişehir, Karabük, Safranbolu, Bursa, Aydın, Söke, Antalya, and Kars. What began 150 years ago in Nikšić now stands as a deeply rooted plane tree across the nation."
  }
];

const NIKŠIĆ_ROOT_FOUNDER_IDS = ["I227", "I226", "I711", "I762", "I758", "I759", "I14", "I15"];

function getCityAssociatedPeople(city, targetYear, branchFilter, eventFilter) {
  if (!state.data || !state.data.individuals) return [];
  const results = [];
  const isBranchAll = !branchFilter || branchFilter === "all";
  const branchSet = !isBranchAll ? getBranchPersonIds(branchFilter) : null;

  for (const [id, p] of Object.entries(state.data.individuals)) {
    if (branchSet && !branchSet.has(id)) continue;

    const bp = normalizeText(p.birthPlace || "");
    const dp = normalizeText(p.deathPlace || "");
    const notes = normalizeText(p.notes || "");
    const isRootFounder = city.id === "niksic" && NIKŠIĆ_ROOT_FOUNDER_IDS.includes(id);

    let matchType = null;
    let eventYearNum = 0;

    const bYear = extractNumericYear(p.birthYear) || 0;
    const dYear = extractNumericYear(p.deathYear) || 0;

    if (isRootFounder) {
      if (eventFilter === "death") continue;
      matchType = "root";
      eventYearNum = bYear || city.era;
    } else {
      const matchesBirth = city.keywords.some((kw) => bp.includes(kw));
      const matchesDeath = city.keywords.some((kw) => dp.includes(kw));
      const matchesNotes = city.keywords.some((kw) => notes.includes(kw));

      if (matchesBirth && (eventFilter === "all" || eventFilter === "birth")) {
        matchType = "birth";
        eventYearNum = bYear || city.era;
      } else if (matchesDeath && (eventFilter === "all" || eventFilter === "death")) {
        matchType = "death";
        eventYearNum = dYear || bYear || city.era;
      } else if (matchesNotes && eventFilter === "all") {
        matchType = "note";
        eventYearNum = bYear || dYear || city.era;
      }
    }

    if (matchType) {
      if (targetYear && eventYearNum > targetYear) continue;
      results.push({
        id,
        person: p,
        matchType,
        year: eventYearNum,
        label:
          matchType === "root"
            ? (state.lang === "tr" ? "👑 Kurucu Ecdat" : "👑 Root Ancestor")
            : matchType === "birth"
            ? (state.lang === "tr" ? "🌱 Doğum" : "🌱 Birth")
            : matchType === "death"
            ? (state.lang === "tr" ? "🕊️ Kabir / Vefat" : "🕊️ Resting Place")
            : (state.lang === "tr" ? "📜 Tarihçe" : "📜 History")
      });
    }
  }

  results.sort((a, b) => (a.year || 9999) - (b.year || 9999));
  return results;
}

function populateMapBranchFilter() {
  const sel = document.getElementById("map-branch-filter");
  if (!sel) return;
  const currentVal = state.mapBranch || "all";
  let html = `<option value="all">${state.lang === "tr" ? "Tüm Kollar" : "All Branches"}</option>`;
  FEATURED_BRANCHES.forEach((b) => {
    const lbl = state.lang === "tr" ? b.labelTr : b.labelEn;
    html += `<option value="${b.id}" ${currentVal === b.id ? "selected" : ""}>${escapeHtml(lbl)}</option>`;
  });
  sel.innerHTML = html;
}

export function renderMigrationMap(resetCamera = false) {
  if (!state.data) return;
  populateMapBranchFilter();

  const svg = document.getElementById("migration-map-svg");
  if (!svg) return;

  const currentYear = state.mapYear || 2026;
  updateMapTimelineUI();

  // 1. Defs & Graticules & Landmass
  let svgContent = `
    <defs>
      <radialGradient id="map-vignette" cx="50%" cy="50%" r="52%">
        <stop offset="65%" stop-color="#fff" stop-opacity="0" />
        <stop offset="100%" stop-color="#6e5737" stop-opacity="0.32" />
      </radialGradient>
      <filter id="map-glow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="3" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    <!-- Sea Background -->
    <rect width="1200" height="680" class="map-sea-bg" />

    <!-- Graticules -->
    <g class="map-graticule-group">
      <line x1="124" y1="0" x2="124" y2="680" class="map-graticule" />
      <line x1="292" y1="0" x2="292" y2="680" class="map-graticule" />
      <line x1="462" y1="0" x2="462" y2="680" class="map-graticule" />
      <line x1="630" y1="0" x2="630" y2="680" class="map-graticule" />
      <line x1="800" y1="0" x2="800" y2="680" class="map-graticule" />
      <line x1="970" y1="0" x2="970" y2="680" class="map-graticule" />
      <line x1="1140" y1="0" x2="1140" y2="680" class="map-graticule" />

      <line x1="0" y1="92" x2="1200" y2="92" class="map-graticule" />
      <line x1="0" y1="216" x2="1200" y2="216" class="map-graticule" />
      <line x1="0" y1="340" x2="1200" y2="340" class="map-graticule" />
      <line x1="0" y1="464" x2="1200" y2="464" class="map-graticule" />
      <line x1="0" y1="588" x2="1200" y2="588" class="map-graticule" />

      <text x="128" y="20" class="map-graticule-text">19°E</text>
      <text x="466" y="20" class="map-graticule-text">27°E</text>
      <text x="634" y="20" class="map-graticule-text">31°E</text>
      <text x="804" y="20" class="map-graticule-text">35°E</text>
      <text x="1144" y="20" class="map-graticule-text">43°E</text>

      <text x="15" y="96" class="map-graticule-text">44°N</text>
      <text x="15" y="220" class="map-graticule-text">42°N</text>
      <text x="15" y="344" class="map-graticule-text">40°N</text>
      <text x="15" y="468" class="map-graticule-text">38°N</text>
      <text x="15" y="592" class="map-graticule-text">36°N</text>
    </g>

    <!-- Balkan Landmass -->
    <path class="map-land-mass" d="
      M 0,0 L 550,0 L 550,270 
      C 545,268 535,270 520,272 
      C 490,275 460,268 440,260
      L 420,250 C 400,240 370,230 350,225
      L 320,230 C 300,235 285,250 270,275
      C 255,300 240,320 225,350
      C 210,380 200,420 190,460
      L 185,500 C 180,530 190,560 210,580
      L 230,590 C 250,600 280,580 290,550
      L 295,510 C 300,480 320,440 340,410
      L 360,380 C 380,360 410,340 430,330
      L 440,300 C 445,280 430,270 410,265
      L 380,260 C 350,255 320,250 290,230
      L 260,210 C 230,190 200,180 180,180
      L 150,185 C 130,190 110,210 90,240
      L 70,270 C 50,300 30,330 0,350 Z" />

    <!-- Anatolia Landmass -->
    <path class="map-land-mass" d="
      M 550,270 
      C 560,268 590,260 620,255 
      C 650,250 680,245 705,248
      C 730,250 770,260 805,270
      C 840,280 870,290 910,295
      L 970,300 C 1020,305 1080,300 1150,295
      L 1200,290 L 1200,680 L 1150,680
      C 1100,650 1050,640 1000,635
      C 940,630 880,620 830,620
      L 760,625 C 700,630 650,635 620,600
      L 600,560 C 590,540 575,520 550,510
      L 520,505 C 495,500 480,485 475,465
      L 470,440 C 465,420 455,400 450,380
      L 455,360 C 460,345 470,335 490,330
      L 520,325 C 540,320 555,310 560,295
      C 565,280 558,272 550,270 Z" />

    <!-- Historical Sea & Region Labels -->
    <text x="110" y="320" class="map-sea-label">ADRİYATİK DENİZİ</text>
    <text x="360" y="490" class="map-sea-label">EGE DENİZİ</text>
    <text x="545" y="300" class="map-sea-label" style="font-size: 11px; letter-spacing: 2px;">MARMARA</text>
    <text x="780" y="180" class="map-sea-label">KARADENİZ</text>
    <text x="680" y="660" class="map-sea-label">AKDENİZ</text>

    <text x="135" y="125" class="map-region-label">KARADAĞ</text>
    <text x="175" y="175" class="map-region-label" style="font-size: 11px;">SANCAK</text>
    <text x="215" y="125" class="map-region-label" style="font-size: 11px;">KOSOVA</text>
    <text x="470" y="350" class="map-region-label" style="font-size: 11px;">KÖRFEZ</text>
    <text x="730" y="380" class="map-region-label">ANADOLU</text>
  `;

  // 2. Migration Arcs
  svgContent += `<g class="migration-arcs-group">`;
  MIGRATION_ROUTES.forEach((r) => {
    const fromCity = MAP_CITIES.find((c) => c.id === r.from);
    const toCity = MAP_CITIES.find((c) => c.id === r.to);
    if (!fromCity || !toCity) return;

    const p1 = projectMapCoord(fromCity.lat, fromCity.lon);
    const p2 = projectMapCoord(toCity.lat, toCity.lon);
    const d = makeArcPath(p1, p2);

    const isPast = r.year <= currentYear;
    const isSelectedRoute =
      state.mapSelectedCity &&
      (r.from === state.mapSelectedCity || r.to === state.mapSelectedCity);

    let cls = "migration-arc";
    if (!isPast) cls += " dimmed";
    if (isSelectedRoute) cls += " highlighted";

    svgContent += `<path d="${d}" class="${cls}" data-from="${r.from}" data-to="${r.to}"><title>${escapeHtml(r.label)} (${r.year})</title></path>`;
  });
  svgContent += `</g>`;

  // 3. City Markers & Pins
  svgContent += `<g class="city-markers-group">`;
  MAP_CITIES.forEach((city) => {
    const p = projectMapCoord(city.lat, city.lon);
    const people = getCityAssociatedPeople(city, currentYear, state.mapBranch, state.mapEvent);
    const count = people.length;

    const isVisibleEra = city.era <= currentYear;
    const isSelected = state.mapSelectedCity === city.id;

    let markerCls = "city-marker";
    if (isSelected) markerCls += " active";
    if (!isVisibleEra) markerCls += " dimmed";

    let pinCls = "city-pin";
    if (city.isRoot) pinCls += " root";
    else if (city.isHub) pinCls += " hub";
    if (isSelected) pinCls += " active";

    const pinRadius = city.isRoot ? 7 : city.isHub ? 6.5 : 5;

    let labelText = `${city.name} (${count})`;
    if (city.isRoot) labelText = `👑 NİKSİÇ (${count})`;
    else if (city.id === "burhaniye") labelText = `🌿 Burhaniye (${count})`;
    else if (city.isHub) labelText = `⭐ ${city.name} (${count})`;

    const textLen = labelText.length;
    const boxW = Math.max(56, Math.round(textLen * 7.4 + 16));
    const boxH = 20;

    const offX = city.offsetX || 0;
    const offY = city.offsetY || -18;

    svgContent += `
      <g class="${markerCls}" data-city-id="${city.id}" transform="translate(${p.x}, ${p.y})">
        <circle r="22" fill="transparent" stroke="none" class="city-touch-pad" />
        ${(city.isRoot || city.isHub) && isVisibleEra ? `<circle r="12" class="city-pulse-ring" />` : ""}
        <circle r="${pinRadius}" class="${pinCls}" />
        <g transform="translate(${offX}, ${offY})">
          <rect x="${-boxW / 2}" y="${-boxH / 2}" width="${boxW}" height="${boxH}" class="city-label-box" rx="4" />
          <text x="0" y="1" class="city-label-text">${escapeHtml(labelText)}</text>
        </g>
      </g>
    `;
  });
  svgContent += `</g>`;

  // 4. Antique Compass Rose (Top-Left Aegean/Ionian)
  svgContent += `
    <g transform="translate(320, 95)" opacity="0.78" pointer-events="none">
      <circle r="30" fill="none" stroke="#8c785f" stroke-width="0.8" stroke-dasharray="3 3" />
      <circle r="20" fill="none" stroke="#c59b27" stroke-width="0.7" />
      <path d="M 0,-32 L 5,-9 L 0,0 L -5,-9 Z" fill="#b32d2e" stroke="#8c785f" stroke-width="0.5" />
      <path d="M 0,32 L 5,9 L 0,0 L -5,9 Z" fill="#8c785f" stroke="#8c785f" stroke-width="0.5" />
      <path d="M 32,0 L 9,5 L 0,0 L 9,-5 Z" fill="#8c785f" stroke="#8c785f" stroke-width="0.5" />
      <path d="M -32,0 L -9,5 L 0,0 L -9,-5 Z" fill="#8c785f" stroke="#8c785f" stroke-width="0.5" />
      <circle r="3" fill="#23201d" />
      <text x="0" y="-36" text-anchor="middle" font-family="'Cormorant Garamond', Georgia, serif" font-size="12" font-weight="700" fill="#b32d2e">N</text>
      <text x="0" y="45" text-anchor="middle" font-family="'Cormorant Garamond', Georgia, serif" font-size="10" fill="#8c785f">S</text>
      <text x="42" y="3" text-anchor="start" dominant-baseline="central" font-family="'Cormorant Garamond', Georgia, serif" font-size="10" fill="#8c785f">E</text>
      <text x="-42" y="3" text-anchor="end" dominant-baseline="central" font-family="'Cormorant Garamond', Georgia, serif" font-size="10" fill="#8c785f">W</text>
    </g>
  `;

  // 5. Ornamental Cartouche (Bottom-Left)
  svgContent += `
    <g transform="translate(35, 555)" opacity="0.92" pointer-events="none">
      <rect x="0" y="0" width="280" height="90" rx="8" class="city-label-box" stroke="#b38600" stroke-width="1.4" filter="drop-shadow(0 4px 12px rgba(0,0,0,0.12))" />
      <rect x="4" y="4" width="272" height="82" rx="6" fill="none" stroke="#c59b27" stroke-width="0.8" stroke-dasharray="4 2" />
      <text x="140" y="24" text-anchor="middle" font-family="'Cormorant Garamond', Georgia, serif" font-size="15" font-weight="700" letter-spacing="1" fill="#7a6240">HOCİÇ &amp; NİKSİÇ AİLESİ</text>
      <text x="140" y="44" text-anchor="middle" font-family="'Cormorant Garamond', Georgia, serif" font-size="12" font-style="italic" fill="#8c785f">Tarihsel Göç ve Yerleşim Atlası</text>
      <line x1="40" y1="53" x2="240" y2="53" stroke="#b38600" stroke-width="0.8" />
      <text x="140" y="70" text-anchor="middle" font-family="'Plus Jakarta Sans', system-ui, sans-serif" font-size="11" font-weight="600" fill="#235340">1870 Niksiç ➔ 2026 Türkiye</text>
    </g>
  `;

  // 6. Vintage Vignette Overlay & Border Frame
  svgContent += `
    <rect width="1200" height="680" fill="url(#map-vignette)" pointer-events="none" />
    <rect x="4" y="4" width="1192" height="672" fill="none" stroke="#7a6240" stroke-width="2" pointer-events="none" />
    <rect x="8" y="8" width="1184" height="664" fill="none" stroke="#c59b27" stroke-width="0.8" stroke-dasharray="6 3" pointer-events="none" />
  `;

  svg.innerHTML = svgContent;

  // Attach city marker hover & click
  svg.querySelectorAll(".city-marker").forEach((el) => {
    const cityId = el.getAttribute("data-city-id");
    if (!cityId) return;

    el.addEventListener("pointerenter", (e) => {
      if (e.pointerType !== "touch") {
        showMapTooltip(cityId, e.clientX, e.clientY);
      }
    });

    el.addEventListener("pointermove", (e) => {
      if (e.pointerType !== "touch") {
        showMapTooltip(cityId, e.clientX, e.clientY);
      }
    });

    el.addEventListener("pointerleave", () => {
      hideMapTooltip();
    });

    el.addEventListener("click", (e) => {
      e.stopPropagation();
      hideMapTooltip();
      openCityInspector(cityId);
    });
  });

  if (resetCamera || !state.mapHasBeenFitted) {
    fitMap();
    state.mapHasBeenFitted = true;
  } else {
    applyMapCamera();
  }
}

function showMapTooltip(cityId, clientX, clientY) {
  const tip = document.getElementById("map-tooltip");
  const wrap = document.getElementById("map-viewport-wrap");
  if (!tip || !wrap) return;

  const city = MAP_CITIES.find((c) => c.id === cityId);
  if (!city) return;

  const currentYear = state.mapYear || 2026;
  const people = getCityAssociatedPeople(city, currentYear, state.mapBranch, state.mapEvent);
  const count = people.length;

  let peopleHtml = "";
  if (count === 0) {
    peopleHtml = `<div style="font-style:italic;color:var(--text-muted);font-size:0.76rem;">${state.lang === "tr" ? "Bu zaman aralığında kayıtlı fert yok" : "No individuals recorded in this era"}</div>`;
  } else {
    const topPeople = people.slice(0, 4);
    peopleHtml = topPeople
      .map(({ person, label }) => {
        const dates = formatLifeYears(person);
        return `
          <div class="map-tip-person-line">
            <span style="color:var(--accent-gold);font-weight:700;">•</span>
            <strong style="color:var(--text-primary);max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(person.name)}</strong>
            ${dates ? `<span style="color:var(--text-muted);font-size:0.72rem;margin-left:auto;">${escapeHtml(dates)}</span>` : ""}
          </div>
        `;
      })
      .join("");

    if (count > 4) {
      peopleHtml += `
        <div style="font-size:0.72rem;color:var(--text-muted);font-style:italic;margin-top:2px;">
          +${count - 4} ${state.lang === "tr" ? "kişi daha..." : "more people..."}
        </div>
      `;
    }
  }

  tip.innerHTML = `
    <div class="map-tip-head">
      <h4 class="map-tip-title">${escapeHtml(city.name)}</h4>
      <span class="map-tip-count">${count} ${state.lang === "tr" ? "Kişi" : "People"}</span>
    </div>
    <div class="map-tip-region">${escapeHtml(city.region)}</div>
    <div class="map-tip-people">${peopleHtml}</div>
    <div class="map-tip-footer">
      <span>${state.lang === "tr" ? "Detaylı liste için tıklayın ➔" : "Click for full details ➔"}</span>
    </div>
  `;

  tip.classList.remove("hidden");

  const wrapRect = wrap.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const tipW = tipRect.width || 240;
  const tipH = tipRect.height || 140;

  let left = clientX - wrapRect.left + 14;
  let top = clientY - wrapRect.top + 14;

  if (left + tipW > wrapRect.width - 12) {
    left = clientX - wrapRect.left - tipW - 14;
  }
  if (top + tipH > wrapRect.height - 12) {
    top = clientY - wrapRect.top - tipH - 14;
  }
  if (left < 10) left = 10;
  if (top < 10) top = 10;

  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

function hideMapTooltip() {
  const tip = document.getElementById("map-tooltip");
  if (tip && !tip.classList.contains("hidden")) {
    tip.classList.add("hidden");
  }
}

function updateMapSelectionHighlight() {
  const svg = document.getElementById("migration-map-svg");
  if (!svg) return;
  const selectedId = state.mapSelectedCity;
  svg.querySelectorAll(".city-marker").forEach((m) => {
    const isThis = m.getAttribute("data-city-id") === selectedId;
    m.classList.toggle("active", isThis);
    const pin = m.querySelector(".city-pin");
    if (pin) pin.classList.toggle("active", isThis);
  });
  svg.querySelectorAll(".migration-arc").forEach((a) => {
    const from = a.getAttribute("data-from");
    const to = a.getAttribute("data-to");
    a.classList.toggle("highlighted", Boolean(selectedId && (from === selectedId || to === selectedId)));
  });
}

function applyMapCamera() {
  const stage = document.getElementById("map-stage");
  if (stage) {
    stage.style.transform = `translate(${state.mapPanX}px, ${state.mapPanY}px) scale(${state.mapScale})`;
  }
}

export function zoomMap(delta) {
  const wrap = document.getElementById("map-viewport-wrap");
  const w = wrap && wrap.clientWidth ? wrap.clientWidth : window.innerWidth;
  const h = wrap && wrap.clientHeight ? wrap.clientHeight : window.innerHeight;
  const cx = w / 2;
  const cy = h / 2;

  const oldScale = state.mapScale || 1;
  const newScale = Math.min(3.5, Math.max(0.35, oldScale + delta));
  const factor = newScale / oldScale;

  state.mapPanX = Math.round(cx - (cx - (state.mapPanX || 0)) * factor);
  state.mapPanY = Math.round(cy - (cy - (state.mapPanY || 0)) * factor);
  state.mapScale = Math.round(newScale * 100) / 100;
  applyMapCamera();
}

export function fitMap() {
  const wrap = document.getElementById("map-viewport-wrap");
  const w = wrap && wrap.clientWidth ? wrap.clientWidth : window.innerWidth;
  const h = wrap && wrap.clientHeight ? wrap.clientHeight : (window.innerHeight - 180);

  const isMobile = window.innerWidth <= 768;

  // The map SVG coordinate canvas is 1200 x 680. Focus on Balkan-to-Anatolia migration zone
  let scale;
  if (isMobile) {
    // Fill the phone screen with the primary historical corridor
    scale = Math.max(0.48, Math.min(1.05, w / 680));
  } else {
    scale = Math.min((w - 40) / 1200, (h - 40) / 680, 1.3);
    scale = Math.max(0.65, scale);
  }

  state.mapScale = Math.round(scale * 100) / 100;
  const cx = isMobile ? 440 : 600;
  const cy = isMobile ? 330 : 340;
  state.mapPanX = Math.round(w / 2 - cx * state.mapScale);
  state.mapPanY = Math.round((isMobile ? h * 0.44 : h / 2) - cy * state.mapScale);
  applyMapCamera();
}

function focusCityOnMap(cityId) {
  const city = MAP_CITIES.find((c) => c.id === cityId);
  if (!city) return;
  const p = projectMapCoord(city.lat, city.lon);
  const wrap = document.getElementById("map-viewport-wrap");
  const w = wrap && wrap.clientWidth ? wrap.clientWidth : window.innerWidth;
  const h = wrap && wrap.clientHeight ? wrap.clientHeight : window.innerHeight;
  const isMobile = window.innerWidth <= 768;
  state.mapScale = isMobile ? 1.4 : 1.35;
  state.mapPanX = Math.round(w / 2 - p.x * state.mapScale);
  state.mapPanY = Math.round((isMobile ? h * 0.40 : h / 2) - p.y * state.mapScale);
  applyMapCamera();
}

export function openCityInspector(cityId) {
  const city = MAP_CITIES.find((c) => c.id === cityId);
  if (!city) return;
  state.mapSelectedCity = cityId;

  const inspector = document.getElementById("map-city-inspector");
  if (!inspector) return;

  const people = getCityAssociatedPeople(city, state.mapYear, state.mapBranch, state.mapEvent);

  const title = document.getElementById("city-inspector-title");
  const sub = document.getElementById("city-inspector-subtitle");
  const role = document.getElementById("city-inspector-role");
  const list = document.getElementById("city-inspector-people-list");

  const personCountLabel = state.lang === "tr" ? `${people.length} Kişi` : `${people.length} People`;
  if (title) title.textContent = city.name;
  if (sub) sub.textContent = `${city.region} • ${personCountLabel}`;
  if (role) role.textContent = city.roleDesc;

  if (list) {
    if (people.length === 0) {
      list.innerHTML = `<p style="font-size:0.85rem;color:var(--text-muted);font-style:italic;padding:0.5rem 0;">${state.lang === "tr" ? "Seçilen zaman aralığında veya dalda kayıtlı kişi bulunamadı." : "No individuals recorded for selected era or branch."}</p>`;
    } else {
      list.innerHTML = people
        .map(({ id, person, label }) => {
          const dates = formatLifeYears(person);
          return `
          <div class="city-person-item">
            <div style="min-width:0;flex:1;">
              <div class="city-person-name">${escapeHtml(person.name)}</div>
              <div class="city-person-meta">
                <span class="focus-badge" style="padding:1px 5px;font-size:0.68rem;margin-right:4px;">${escapeHtml(label)}</span>
                ${dates ? `<span>${escapeHtml(dates)}</span>` : ""}
              </div>
            </div>
            <div style="display:flex;gap:4px;">
              <button type="button" class="mini-btn" data-action="focus-tree" data-pid="${id}" title="${state.lang === "tr" ? "Soy Ağacında Odakla" : "Focus in Tree"}">🌳</button>
              <button type="button" class="mini-btn" data-action="open-drawer" data-pid="${id}" title="${state.lang === "tr" ? "Kişi Kartını Aç" : "Open Drawer"}">👤</button>
            </div>
          </div>
        `;
        })
        .join("");

      list.querySelectorAll("[data-action='focus-tree']").forEach((btn) => {
        btn.addEventListener("click", () => {
          mapCtx.switchView("tree");
          mapCtx.focusPersonInTree(btn.dataset.pid);
        });
      });
      list.querySelectorAll("[data-action='open-drawer']").forEach((btn) => {
        btn.addEventListener("click", () => {
          mapCtx.openPersonDrawer(btn.dataset.pid);
        });
      });
    }
  }

  inspector.classList.remove("hidden");
  updateMapSelectionHighlight();
}

export function closeCityInspector() {
  state.mapSelectedCity = null;
  const inspector = document.getElementById("map-city-inspector");
  if (inspector) inspector.classList.add("hidden");
  updateMapSelectionHighlight();
}

export function updateMapTimelineUI() {
  const slider = document.getElementById("map-year-slider");
  if (slider && parseInt(slider.value, 10) !== state.mapYear) {
    slider.value = String(state.mapYear);
  }
  const yearLbl = document.getElementById("map-current-year-label");
  if (yearLbl) yearLbl.textContent = String(state.mapYear);

  const eraBadge = document.getElementById("map-active-era-badge");
  if (eraBadge) eraBadge.textContent = `1870 — ${state.mapYear}`;
}

export function toggleMapTimelinePlay() {
  if (state.mapPlaying) {
    stopMapTimelinePlay();
  } else {
    startMapTimelinePlay();
  }
}

function startMapTimelinePlay() {
  state.mapPlaying = true;
  const icon = document.getElementById("map-play-icon");
  if (icon) icon.textContent = "⏸";
  if (state.mapYear >= 2026) state.mapYear = 1870;

  state.mapPlayTimer = setInterval(() => {
    if (state.mapYear < 2026) {
      state.mapYear = Math.min(2026, state.mapYear + 2);
      updateMapTimelineUI();
      renderMigrationMap(false);
    } else {
      stopMapTimelinePlay();
    }
  }, 110);
}

function stopMapTimelinePlay() {
  state.mapPlaying = false;
  const icon = document.getElementById("map-play-icon");
  if (icon) icon.textContent = "▶";
  if (state.mapPlayTimer) {
    clearInterval(state.mapPlayTimer);
    state.mapPlayTimer = null;
  }
}

export function startStoryTour() {
  state.mapStoryActive = true;
  state.mapStoryStep = 0;
  showStoryStep(0);
}

function showStoryStep(idx) {
  if (idx < 0) idx = 0;
  if (idx >= MAP_STORY_CHAPTERS.length) idx = MAP_STORY_CHAPTERS.length - 1;
  state.mapStoryStep = idx;

  const ch = MAP_STORY_CHAPTERS[idx];
  const card = document.getElementById("map-story-card");
  if (!card) return;

  const badge = document.getElementById("story-step-badge");
  const title = document.getElementById("story-card-title");
  const text = document.getElementById("story-card-text");
  const prevBtn = document.getElementById("story-prev-btn");
  const nextBtn = document.getElementById("story-next-btn");

  if (badge) badge.textContent = `${idx + 1} / ${MAP_STORY_CHAPTERS.length}`;
  if (title) title.textContent = state.lang === "tr" ? ch.titleTr : ch.titleEn;
  if (text) text.textContent = state.lang === "tr" ? ch.textTr : ch.textEn;

  if (prevBtn) prevBtn.disabled = idx === 0;
  if (nextBtn) {
    nextBtn.textContent =
      idx === MAP_STORY_CHAPTERS.length - 1
        ? (state.lang === "tr" ? "Tamamla 🏁" : "Finish 🏁")
        : (state.lang === "tr" ? "Sonraki ▶" : "Next ▶");
  }

  state.mapYear = ch.year;
  updateMapTimelineUI();

  state.mapSelectedCity = ch.focusCity;
  if (ch.focusCity) {
    focusCityOnMap(ch.focusCity);
  } else {
    state.mapScale = ch.scale;
    state.mapPanX = ch.panX;
    state.mapPanY = ch.panY;
    applyMapCamera();
  }
  renderMigrationMap(false);

  card.classList.remove("hidden");
}

export function prevStoryStep() {
  if (state.mapStoryStep > 0) showStoryStep(state.mapStoryStep - 1);
}

export function nextStoryStep() {
  if (state.mapStoryStep < MAP_STORY_CHAPTERS.length - 1) {
    showStoryStep(state.mapStoryStep + 1);
  } else {
    closeStoryTour();
  }
}

export function closeStoryTour() {
  state.mapStoryActive = false;
  const card = document.getElementById("map-story-card");
  if (card) card.classList.add("hidden");
  fitMap();
  renderMigrationMap(false);
}

export function locateCityOnMap(cityName) {
  if (!cityName) return;
  const norm = normalizeText(cityName);
  const city = MAP_CITIES.find((c) => {
    if (normalizeText(c.name).includes(norm) || norm.includes(normalizeText(c.name))) return true;
    return c.keywords.some((kw) => norm.includes(kw));
  });

  if (!city) {
    alert(
      state.lang === "tr"
        ? `"${cityName}" haritada henüz konumlandırılmadı.`
        : `"${cityName}" is not yet mapped.`
    );
    return;
  }

  mapCtx.closePersonDrawer();
  mapCtx.switchView("map");
  state.mapYear = 2026;
  updateMapTimelineUI();
  focusCityOnMap(city.id);
  openCityInspector(city.id);
}

export function initMapPanZoomGestures() {
  const wrap = document.getElementById("map-viewport-wrap");
  const stage = document.getElementById("map-stage");
  if (!wrap || !stage) return;

  const mapPointers = new Map();
  let mapPinchDist = 0;
  let mapPinchMid = null;

  wrap.addEventListener("pointerdown", (e) => {
    hideMapTooltip();
    if (e.target.closest("button, input, select, .map-story-card, .map-city-inspector, .city-marker, .canvas-hud")) return;

    mapPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try {
      wrap.setPointerCapture(e.pointerId);
    } catch (_) {}

    if (mapPointers.size === 1) {
      state.mapIsDragging = true;
      state.mapDragStartX = e.clientX - (state.mapPanX || 0);
      state.mapDragStartY = e.clientY - (state.mapPanY || 0);
      stage.classList.add("is-dragging");
    } else if (mapPointers.size === 2) {
      state.mapIsDragging = false;
      stage.classList.remove("is-dragging");
      const [p1, p2] = Array.from(mapPointers.values());
      mapPinchDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      mapPinchMid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    }
  });

  wrap.addEventListener("click", (e) => {
    if (!e.target.closest(".city-marker, .map-story-card, .map-city-inspector, button, .canvas-hud")) {
      hideMapTooltip();
    }
  });

  wrap.addEventListener("pointermove", (e) => {
    if (!mapPointers.has(e.pointerId)) return;
    mapPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (mapPointers.size === 2) {
      const [p1, p2] = Array.from(mapPointers.values());
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };

      if (mapPinchDist > 8) {
        const factor = dist / mapPinchDist;
        const oldScale = state.mapScale || 1;
        const newScale = Math.min(3.5, Math.max(0.35, oldScale * factor));
        const effectiveFactor = newScale / oldScale;

        state.mapPanX = Math.round(mid.x - (mid.x - (state.mapPanX || 0)) * effectiveFactor);
        state.mapPanY = Math.round(mid.y - (mid.y - (state.mapPanY || 0)) * effectiveFactor);
        state.mapScale = Math.round(newScale * 100) / 100;
        applyMapCamera();
      }
      if (mapPinchMid) {
        state.mapPanX += Math.round(mid.x - mapPinchMid.x);
        state.mapPanY += Math.round(mid.y - mapPinchMid.y);
        applyMapCamera();
      }
      mapPinchDist = dist;
      mapPinchMid = mid;
    } else if (mapPointers.size === 1 && state.mapIsDragging) {
      state.mapPanX = Math.round(e.clientX - state.mapDragStartX);
      state.mapPanY = Math.round(e.clientY - state.mapDragStartY);
      applyMapCamera();
    }
  });

  const stopDrag = (e) => {
    mapPointers.delete(e.pointerId);
    if (mapPointers.size < 2) {
      mapPinchDist = 0;
      mapPinchMid = null;
    }
    if (mapPointers.size === 1) {
      const rem = Array.from(mapPointers.values())[0];
      state.mapIsDragging = true;
      state.mapDragStartX = rem.x - (state.mapPanX || 0);
      state.mapDragStartY = rem.y - (state.mapPanY || 0);
      stage.classList.add("is-dragging");
    } else if (mapPointers.size === 0) {
      state.mapIsDragging = false;
      stage.classList.remove("is-dragging");
    }
    try {
      wrap.releasePointerCapture(e.pointerId);
    } catch (_) {}
  };

  wrap.addEventListener("pointerup", stopDrag);
  wrap.addEventListener("pointercancel", stopDrag);

  wrap.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 0.15 : -0.15;
      zoomMap(zoomFactor);
    },
    { passive: false }
  );
}
